<template>
  <div class="text-element-wrapper">
    <div class="element-header">
      <span class="element-type-badge">
        <i class="fas fa-comment"></i>
        <span>Message</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <i class="fas fa-copy"></i>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
    
    <div class="message-content" @click.stop="handleMessageContentClick">
      <!-- Mode affichage du message -->
      <div class="message-display-container" v-if="!isEditingMessage" @click="startEditMessage">
        <div class="message-bubble">
          <p class="message-text" v-if="element.text || element.message" v-html="formatMessage(element.text || element.message)"></p>
          <p class="message-placeholder" v-else>Cliquez pour ajouter un message...</p>
          <div class="message-time">{{ formattedTime }}</div>
        </div>
        <span class="char-count" :class="{'char-count-limit': (element.text || element.message || '').length >= 640}">{{ (element.text || element.message || '').length }}/640</span>
      </div>
      
      <!-- Mode édition du message -->
      <div v-if="isEditingMessage" class="message-editor-container">
        <!-- Emoji button container au-dessus du textarea -->
        <div class="emoji-button-container">
          <button type="button" class="emoji-button" @click.stop.prevent="$event => toggleEmojiPicker($event)">
            <i class="far fa-smile"></i>
          </button>
          
          <!-- Sélecteur d'emojis -->
          <div v-if="showEmojiPicker" class="emoji-picker">
            <div class="emoji-picker-content">
              <div class="emoji-category">
                <button 
                  v-for="emoji in commonEmojis" 
                  :key="emoji" 
                  type="button"
                  class="emoji-btn" 
                  @mousedown.prevent="(event) => insertEmoji(emoji, event)"
                >
                  {{ emoji }}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div class="message-editor-wrapper">
          <textarea 
            ref="messageEditor"
            v-model="editedMessage" 
            class="message-editor" 
            placeholder="Écrivez votre message ici..."
            @input="handleMessageInput"
            @keydown="handleMessageKeydown"
            @blur="handleBlur"
            maxlength="640"
          ></textarea>
          <span class="char-count" :class="{ 'char-count-limit': editedMessage.length >= 640 }">
            {{ editedMessage.length }}/640
          </span>
          
          <!-- Suggestions de variables -->
          <div v-if="showVariableSuggestions" class="variable-suggestions">
            <button 
              v-for="(variable, index) in filteredVariableSuggestions" 
              :key="index"
              type="button"
              class="variable-suggestion-item"
              @mousedown.prevent="(event) => insertVariable(variable, event)"
            >
              {{variable.id}}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isEditingMessage: false,
      editedMessage: '',
      showEmojiPicker: false,
      showVariableSuggestions: false,
      variableSuggestionQuery: '',
      cursorPosition: 0,
      isEmojiInteraction: false,
      isInsertingEmoji: false,
      isClickInsideMessageContent: false,
      commonEmojis: ['😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😆', '😉', '😊', '😋', '😎', '😍', '😘', '🥰', '😗', '😙', '😚', '🙂', '🤗', '🤩', '🤔', '🤨', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '😴', '😌', '😛', '😜', '😝', '🤤', '😒', '😓', '😔', '😕', '🙃', '🤑', '😲', '☹️', '🙁', '😖', '😞', '😟', '😤', '😢', '😭', '😦', '😧', '😨', '😩', '🤯', '😬', '😰', '😱', '🥵', '🥶', '😳', '🤪', '😵', '😡', '😠', '🤬', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '😇', '🥳', '🥴', '🥺', '🤠', '🤡', '🤥', '🤫', '🤭', '🧐', '🤓', '😈', '👿', '👹', '👺', '💀', '👻', '👽', '🤖', '💩', '👍', '👎', '👌', '✌️', '🤞', '🤘', '🤙', '👈', '👉', '☝️', '👆', '👇', '✋', '🤚', '🖐️', '🖖', '👋', '🤟', '✍️', '👏', '👐', '🙌', '🤲', '🙏', '🤝', '💪', '💄', '💋', '👄', '👅', '👂', '👃', '👁️', '👀', '🧠', '👶', '👦', '👧', '👨', '👩', '👴', '👵', '🤱', '🤰', '👼', '🎅', '🤶', '🦸', '🦹', '🧙', '🧚', '🧛', '🧜', '🧝', '🧞', '🧟', '💁', '🙅', '🙆', '🙋', '🙇', '🤦', '🤷', '💆', '💇', '🚶', '🏃', '💃', '🕺', '🤺', '🏇', '⛷️', '🏂', '🏌️', '🏄', '🚣', '🏊', '⛹️', '🏋️', '🚴', '🚵', '🤸', '🤼', '🤽', '🤾', '🤹', '❤️', '💚', '💙', '💜', '🧡', '💛', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓']
    };
  },
  computed: {
    formattedTime() {
      return new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    },
    filteredVariableSuggestions() {
      if (!this.variableSuggestionQuery) {
        return this.variables;
      }
      
      return this.variables.filter(variable => 
        variable.id.toLowerCase().includes(this.variableSuggestionQuery.toLowerCase())
      );
    }
  },
  created() {
    // Créer une version liée de closeEmojiPickerOnClickOutside pour préserver le contexte this
    this.boundCloseEmojiPicker = this.closeEmojiPickerOnClickOutside.bind(this);
    this.boundHandleGlobalClick = this.handleGlobalClick.bind(this);
  },
  beforeDestroy() {
    // Nettoyer les écouteurs d'événements lors de la destruction du composant
    document.removeEventListener('click', this.boundCloseEmojiPicker);
    document.removeEventListener('click', this.boundHandleGlobalClick);
  },
  methods: {
    startEditMessage() {
      this.isEditingMessage = true;
      this.editedMessage = this.element.text || this.element.message || '';
      
      // Ajouter un écouteur global pour les clics en dehors du conteneur de message
      document.addEventListener('click', this.boundHandleGlobalClick);
      
      this.$nextTick(() => {
        if (this.$refs.messageEditor) {
          this.$refs.messageEditor.focus();
          this.cursorPosition = this.editedMessage.length;
        }
      });
    },
    
    saveEditedMessage() {
      const updatedElement = { 
        ...this.element, 
      
        message: this.editedMessage 
      };
      this.$emit('update', updatedElement);
      
      this.isEditingMessage = false;
      this.showEmojiPicker = false;
      this.showVariableSuggestions = false;
      
      // Nettoyer les écouteurs d'événements
      document.removeEventListener('click', this.boundHandleGlobalClick);
    },
    
    cancelEditMessage() {
      this.isEditingMessage = false;
      this.showEmojiPicker = false;
      this.showVariableSuggestions = false;
      
      // Nettoyer les écouteurs d'événements
      document.removeEventListener('click', this.boundHandleGlobalClick);
    },
    
    handleMessageInput(event) {
      // Mettre à jour la position du curseur
      this.cursorPosition = event.target.selectionStart;
      
      // Vérifier si l'utilisateur est en train de taper une variable
      const textBeforeCursor = this.editedMessage.substring(0, this.cursorPosition);
      const match = textBeforeCursor.match(/{{([^}]*)$/); // Cherche {{ suivi de caractères jusqu'au curseur
      
      if (match) {
        this.showVariableSuggestions = true;
        this.variableSuggestionQuery = match[1];
      } else {
        this.showVariableSuggestions = false;
      }
    },
    
    handleMessageKeydown(event) {
      // Gérer la touche Tab pour l'autocomplétion
      if (event.key === 'Tab' && this.showVariableSuggestions && this.filteredVariableSuggestions.length > 0) {
        event.preventDefault();
        this.insertVariable(this.filteredVariableSuggestions[0]);
      }
      
      // Si Shift + Enter est pressé, on sauvegarde le message
      if (event.key === 'Enter' && event.shiftKey) {
        event.preventDefault();
        this.saveEditedMessage();
        return;
      }

      // Si Enter est pressé seul, on permet le retour à la ligne
      if (event.key === 'Enter' && !event.shiftKey) {
        return; // Laisse le comportement par défaut du textarea (retour à la ligne)
      }

      // Si Escape est pressé, on annule l'édition
      if (event.key === 'Escape') {
        event.preventDefault();
        this.cancelEditMessage();
      }
    },
    
    handleMessageContentClick() {
      // Marquer que le clic est à l'intérieur du conteneur de message
      this.isClickInsideMessageContent = true;
      
      // Réinitialiser le marqueur après un court délai
      setTimeout(() => {
        this.isClickInsideMessageContent = false;
      }, 100);
    },
    
    handleBlur(event) {
      // Ne pas traiter l'événement blur si nous sommes en train d'insérer un emoji
      if (this.isInsertingEmoji) {
        // Ne rien faire, l'insertion d'emoji gérera le focus
        return;
      }
      
      // Utiliser un délai pour permettre aux événements de clic de se produire d'abord
      setTimeout(() => {
        // Vérifier si le clic est sur un élément lié aux emojis ou aux variables
        const isEmojiRelated = document.activeElement && (
          document.activeElement.closest('.emoji-button') ||
          document.activeElement.closest('.emoji-picker') ||
          document.activeElement.closest('.emoji')
        );
        
        const isVariableRelated = document.activeElement && (
          document.activeElement.closest('.variable-suggestions') ||
          document.activeElement.closest('.variable-suggestion-item')
        );
        
        // Si le clic est à l'intérieur du conteneur de message ou sur un élément lié aux emojis/variables, ne pas sauvegarder
        if (this.isClickInsideMessageContent || isEmojiRelated || isVariableRelated) {
          // Redonner le focus au textarea
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
          return;
        }
        
        // Si le clic est en dehors du conteneur de message et pas sur un élément lié aux emojis/variables, sauvegarder le message
        this.saveEditedMessage();
      }, 100);
    },
    
    toggleEmojiPicker(event) {
      // Empêcher la propagation de l'événement pour éviter le blur du textarea
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      this.isEmojiInteraction = true;
      this.showEmojiPicker = !this.showEmojiPicker;
      this.showVariableSuggestions = false;
  
      if (this.showEmojiPicker) {
        // Ajouter un gestionnaire de clic global pour fermer le picker
        this.$nextTick(() => {
          // Utiliser setTimeout pour éviter que le clic actuel ne ferme immédiatement le picker
          setTimeout(() => {
            document.addEventListener('click', this.boundCloseEmojiPicker);
          }, 0);
        });
      } else {
        // Retirer le gestionnaire si on ferme le picker
        document.removeEventListener('click', this.boundCloseEmojiPicker);
      }
  
      // Redonner le focus au textarea après un court délai
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
        }, 10);
      });
    },
    
    closeEmojiPickerOnClickOutside(event) {
      // Vérifier si le sélecteur d'emojis est ouvert
      if (!this.showEmojiPicker) return;
      
      // Vérifier si le clic est sur le bouton emoji, sur le sélecteur d'emojis ou sur un emoji
      if (event.target.closest('.emoji-picker') || 
          event.target.closest('.emoji-button') || 
          event.target.closest('.emoji')) {
        return;
      }
      
      // Si on arrive ici, c'est qu'on a cliqué en dehors du picker et du bouton
      this.showEmojiPicker = false;
      document.removeEventListener('click', this.boundCloseEmojiPicker);
      
      // Redonner le focus au textarea après un court délai
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
        }, 10);
      });
    },

    handleGlobalClick(event) {
      // Ne rien faire si nous ne sommes pas en mode édition
      if (!this.isEditingMessage) {
        document.removeEventListener('click', this.boundHandleGlobalClick);
        return;
      }
      
      // Vérifier si le clic est à l'intérieur du conteneur de message
      const isClickInsideMessageContent = event.target.closest('.message-content');
      const isClickOnEmojiOrVariable = event.target.closest('.emoji-picker') || 
                                      event.target.closest('.emoji-button') || 
                                      event.target.closest('.emoji') ||
                                      event.target.closest('.variable-suggestions') || 
                                      event.target.closest('.variable-suggestion-item');
      
      // Si le clic est en dehors du conteneur de message et pas sur un emoji ou une variable, sauvegarder le message
      if (!isClickInsideMessageContent && !isClickOnEmojiOrVariable && !this.isInsertingEmoji) {
        this.saveEditedMessage();
      }
    },
    
    insertEmoji(emoji, event) {
      // Empêcher tout comportement par défaut et propagation
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      // Indiquer que nous sommes en train d'insérer un emoji
      this.isInsertingEmoji = true;
      
      // Capturer la position actuelle du curseur au moment de l'insertion
      if (this.$refs.messageEditor) {
        this.cursorPosition = this.$refs.messageEditor.selectionStart;
      }
      
      // Insérer l'emoji à la position du curseur
      const start = this.editedMessage.substring(0, this.cursorPosition);
      const end = this.editedMessage.substring(this.cursorPosition);
      this.editedMessage = start + emoji + end;
      
      // Fermer le sélecteur d'emoji mais garder le mode édition actif
      this.showEmojiPicker = false;
      document.removeEventListener('click', this.boundCloseEmojiPicker);
      
      // Utiliser un setTimeout pour s'assurer que le focus est maintenu
      // et pour éviter que l'événement blur ne se déclenche avant que nous ayons fini
      this.$nextTick(() => {
        setTimeout(() => {
          // Réinitialiser le flag après un court délai
          this.isInsertingEmoji = false;
          
          // Mettre à jour la position du curseur après l'emoji
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
            const newPosition = this.cursorPosition + emoji.length;
            this.$refs.messageEditor.selectionStart = newPosition;
            this.$refs.messageEditor.selectionEnd = newPosition;
            this.cursorPosition = newPosition;
          }
        }, 100);
      });
    },
    
    insertVariable(variable, event) {
      // Empêcher tout comportement par défaut et propagation
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      // Indiquer que nous sommes en train d'insérer une variable
      this.isInsertingEmoji = true; // On utilise la même variable que pour les emojis
      const textBeforeCursor = this.editedMessage.substring(0, this.cursorPosition);
      const textAfterCursor = this.editedMessage.substring(this.cursorPosition);
      
      // Trouver où commence la variable en cours de saisie
      const match = textBeforeCursor.match(/{{([^}]*)$/); 
      
      if (match) {
        const startPos = textBeforeCursor.lastIndexOf('{{');
        const variableText = `{{${variable.id}}}`;
        
        // Remplacer le début de la variable par la variable complète
        this.editedMessage = textBeforeCursor.substring(0, startPos) + variableText + textAfterCursor;
        
        // Mettre à jour la position du curseur après la variable insérée
        this.$nextTick(() => {
          setTimeout(() => {
            // Réinitialiser le flag après un court délai
            this.isInsertingEmoji = false;
            
            if (this.$refs.messageEditor) {
              const newCursorPos = startPos + variableText.length;
              this.$refs.messageEditor.focus();
              this.$refs.messageEditor.selectionStart = newCursorPos;
              this.$refs.messageEditor.selectionEnd = newCursorPos;
              this.cursorPosition = newCursorPos;
            }
          }, 100);
        });
      } else {
        // Si pas de variable en cours, insérer à la position du curseur
        const variableText = `{{${variable.id}}}`;
        this.editedMessage = textBeforeCursor + variableText + textAfterCursor;
        
        // Mettre à jour la position du curseur
        this.$nextTick(() => {
          setTimeout(() => {
            // Réinitialiser le flag après un court délai
            this.isInsertingEmoji = false;
            
            if (this.$refs.messageEditor) {
              const newCursorPos = this.cursorPosition + variableText.length;
              this.$refs.messageEditor.focus();
              this.$refs.messageEditor.selectionStart = newCursorPos;
              this.$refs.messageEditor.selectionEnd = newCursorPos;
              this.cursorPosition = newCursorPos;
            }
          }, 100);
        });
      }
      
      this.showVariableSuggestions = false;
    },
    formatMessage(text) {
      if (!text) return '';
      
      // Remplacer les sauts de ligne par des <br>
      let formattedText = text.replace(/\n/g, '<br>');
      
      // Mettre en évidence les variables {{variable}} avec un style distinct
      formattedText = formattedText.replace(/{{([^}]+)}}/g, '<span class="variable-highlight">{{$1}}</span>');
      
      return formattedText;
    }
  }
}
</script>

<style scoped>
.text-element-wrapper {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(249, 250, 251, 0.9));
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  margin-bottom: 20px;
}

.text-element-wrapper:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(145deg, rgba(249, 250, 251, 0.8), rgba(243, 244, 246, 0.6));
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #6b7280;
  font-size: 0.875rem;
}

.element-type-badge i {
  color: #6b7280;
}

.element-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.action-btn.delete-btn:hover {
  background: linear-gradient(145deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.05));
  color: #ef4444;
}

.message-content {
  padding: 16px;
  cursor: text;
  min-height: 60px;
}

.message-display-container {
  position: relative;
  min-height: 60px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.message-display-container:hover {
  background: linear-gradient(135deg, rgba(156, 163, 175, 0.03), rgba(156, 163, 175, 0.01));
  border-radius: 8px;
}

.message-bubble {
  position: relative;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  margin-bottom: 8px;
}

.message-text {
  margin: 0;
  padding: 0;
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.5;
  word-wrap: break-word;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 400;
  white-space: pre-wrap;
}

/* Style pour mettre en évidence les variables */
.message-text .variable-highlight {
  background: linear-gradient(145deg, rgba(156, 163, 175, 0.15), rgba(156, 163, 175, 0.05));
  color: #4b5563;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  border: 1px solid rgba(156, 163, 175, 0.2);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
}

.message-placeholder {
  margin: 0;
  padding: 0;
  color: #9ca3af;
  font-size: 0.95rem;
  line-height: 1.5;
  font-style: italic;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.message-time {
  font-size: 0.75rem;
  color: #6b7280;
  text-align: right;
  margin-top: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.char-count {
  position: absolute;
  right: 0;
  bottom: 0;
  font-size: 0.75rem;
  color: #6b7280;
  background: linear-gradient(145deg, rgba(249, 250, 251, 0.9), rgba(243, 244, 246, 0.8));
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border: 1px solid rgba(209, 213, 219, 0.2);
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.char-count-limit {
  color: #ef4444;
  background: linear-gradient(145deg, rgba(254, 242, 242, 0.9), rgba(254, 226, 226, 0.8));
  border-color: rgba(239, 68, 68, 0.3);
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.2); }
  70% { box-shadow: 0 0 0 5px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}

/* Styles pour l'éditeur de message */
.message-editor-container {
  position: relative;
  margin: 0;
  padding: 0;
  animation: fadeIn 0.3s ease;
  background-color: transparent;
  min-height: 120px;
}

.message-editor-wrapper {
  position: relative;
  background-color: transparent;
  padding: 0;
  margin: 0;
}

.message-editor {
  width: 100%;
  min-height: 120px;
  padding: 16px;
  padding-right: 50px; /* Espace pour le bouton emoji */
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  font-size: 0.95rem;
  line-height: 1.5;
  resize: none;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #1f2937;
  white-space: pre-wrap;
  word-break: break-word;
  font-weight: 400;
  outline: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.message-editor:focus {
  outline: none;
  border-color: #9ca3af;
  box-shadow: 0 0 0 3px rgba(156, 163, 175, 0.1);
}

.emoji-button-container {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
}

.emoji-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.emoji-button:hover {
  background: linear-gradient(145deg, rgba(156, 163, 175, 0.15), rgba(156, 163, 175, 0.05));
  color: #6b7280;
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(156, 163, 175, 0.2);
}

.emoji-picker {
  position: absolute;
  top: 100%;
  right: 0;
  width: 300px;
  max-height: 250px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(249, 250, 251, 0.9));
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 8px;
  animation: fadeIn 0.2s ease;
  overflow-y: auto;
  transform-origin: top right;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.emoji-picker-content {
  padding: 12px;
}

.emoji-category {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.emoji-btn {
  cursor: pointer;
  font-size: 18px;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  background: none;
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
}

.emoji-btn:hover {
  background: linear-gradient(145deg, rgba(156, 163, 175, 0.15), rgba(156, 163, 175, 0.05));
  transform: scale(1.1);
  border-radius: 8px;
}

.emoji-btn:focus {
  background: linear-gradient(145deg, rgba(156, 163, 175, 0.2), rgba(156, 163, 175, 0.1));
  outline: none;
}

/* Styles pour les suggestions de variables */
.variable-suggestions {
  position: fixed !important;
  top: auto !important;
  left: auto !important;
  bottom: 20px !important;
  right: 20px !important;
  width: 300px !important;
  max-height: 150px !important;
  background: #fafafa !important;
  border: 1px solid rgba(59, 130, 246, 0.15) !important;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03) !important;
  z-index: 99999 !important;
  margin: 0 !important;
  overflow-y: auto;
  animation: fadeIn 0.2s ease;
  padding: 16px !important;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.variable-suggestion-item {
  padding: 4px 16px !important;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #ffffff !important;
  border: 1px solid #10b981 !important;
  outline: none;
  border-radius: 8px !important;
  font-size: 12px !important;
  
  color: #1f2937 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 20px !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2) !important;
  text-transform: lowercase !important;
}

.variable-suggestion-item:hover {
  background: #10b981 !important;
  color: white !important;
  border-color: #ffffff !important;
  transform: translateY(-3px) scale(1.05) !important;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3) !important;
}

.variable-suggestion-item:focus {
  background: #059669 !important;
  color: white !important;
  border-color: #ffffff !important;
  outline: 3px solid #fbbf24 !important;
  outline-offset: 2px;
}

@keyframes fadeIn {
  from { 
    opacity: 0; 
    transform: translateY(-10px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
}

/* Styles responsifs */
@media (max-width: 768px) {
  .text-element-wrapper {
    margin-bottom: 16px;
  }
  
  .element-header {
    padding: 10px 12px;
  }
  
  .message-content {
    padding: 12px;
  }
  
  .message-bubble {
    padding: 12px;
  }
  
  .emoji-picker {
    width: 250px;
    max-height: 200px;
  }
  
  .emoji-btn {
    min-width: 28px;
    min-height: 28px;
    font-size: 16px;
  }
}
</style>
